import { Routes } from '@angular/router';
import { ActivateAdmin } from './services/common/activate-admin.service';
import { ActivateAnonymous } from './services/common/activate-anonymous.service';
import { ActivateMarketing } from './services/common/activate-marketing.service';
import { ActivateUsers } from './services/common/activate-users.service';
import { SidebarLayout } from './core/layouts/sidebar-layout';
import { AppComponent } from './app.component';
import { AuthLayout } from './core/layouts/auth-layout';
import { HeaderLayout } from './core/layouts/header-layout';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./authentication/signin/signin.component').then((m) => m.SigninComponent),
    canActivate: [ActivateAnonymous],
  },
  {
    path: 'old',
    component: AppComponent,
    children: [
      {
        path: 'myapplications',
        loadComponent: () => import('./mypplications/mypplications.component').then((m) => m.MyApplicationsComponent),
        data: { title: 'Menu.MyApplications' },
        canActivate: [ActivateUsers],
      },
    ],
  },
  {
    path: '',
    component: AuthLayout,
    children: [
      {
        path: 'auth/logout',
        loadComponent: () => import('./authentication/logout/logout.component').then((m) => m.LogoutComponent),
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'auth-callback',
        loadComponent: () =>
          import('./authentication/auth-callback/auth-callback.component').then((m) => m.AuthCallbackComponent),
        canActivate: [ActivateAnonymous],
      },
    ],
  },
  {
    // Header Layout
    path: '',
    component: HeaderLayout,
    children: [
      {
        path: 'auth/change-password',
        loadComponent: () =>
          import('./authentication/change-password/change-password.component').then((m) => m.ChangePasswordComponent),
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'auth/change-password/:isSecondFactor',
        loadComponent: () =>
          import('./authentication/change-password/change-password.component').then((m) => m.ChangePasswordComponent),
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'auth/change-password/:sequence/:isSecondFactor',
        loadComponent: () =>
          import('./authentication/change-password/change-password.component').then((m) => m.ChangePasswordComponent),
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'verify-user/:isAskAnswer/:isChangePass',
        loadComponent: () =>
          import('./authentication/security-questions/security-questions-container/security-questions-container.component').then(
            (m) => m.SecurityQuestionsContainerComponent,
          ),
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'awaiting-enrollment',
        loadComponent: () =>
          import('./authentication/awaiting-enrollment/awaiting-enrollment.component').then(
            (m) => m.AwaitingEnrollmentComponent,
          ),
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'credential-reset/enrollment',
        loadComponent: () =>
          import('./credential-reset/credential-reset-enrollment/credential-reset-enrollment.component').then(
            (m) => m.CredentialResetEnrollmentComponent,
          ),
        data: { title: 'Menu.CredentialReset.Enrollment' },
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'enforce-enrollment',
        loadComponent: () =>
          import('./authentication/enforce-enrollment/enforce-enrollment.component').then(
            (m) => m.EnforceEnrollmentComponent,
          ),
        canActivate: [ActivateAnonymous],
      },
      {
        path: 'styled-message/:message',
        loadComponent: () =>
          import('./shared/styled-message/styled-message.component').then((m) => m.StyledMessageComponent),
      },
    ],
  },
  {
    // Sidebar Layout - Anonymous
    path: '',
    canActivate: [ActivateAnonymous],
    component: SidebarLayout,
    data: { showLanguageSwitcher: true },
    children: [
      {
        path: 'login',
        loadComponent: () => import('./authentication/signin/signin.component').then((m) => m.SigninComponent),
      },
      {
        path: 'signin',
        loadComponent: () =>
          import('./authentication/signin-redirect/signin-redirect.component').then((m) => m.SigninRedirectComponent),
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import('./authentication/signin-redirect/signin-redirect.component').then((m) => m.SigninRedirectComponent),
      },
      {
        path: 'azure-redirect',
        loadComponent: () => import('./cloud/azure/custom.redirection').then((m) => m.AzureCustomRedirectionComponent),
      },
      {
        path: 'cloud/azure/custom-redirection',
        loadComponent: () => import('./cloud/azure/custom.redirection').then((m) => m.AzureCustomRedirectionComponent),
      },
      {
        path: 'cloud/google/auth-iframe',
        loadComponent: () =>
          import('./cloud/google-auth-iframe/google-auth-iframe.component').then((m) => m.GoogleAuthIframeComponent),
      },
      {
        path: 'organization/licence/update/:method/:orgName',
        loadComponent: () =>
          import('./enterprise/organization/licence/update-expired/update-expired.component').then(
            (m) => m.UpdateExpiredComponent,
          ),
        data: { title: 'Organization.UpdateLicence' },
      },
    ],
  },
  {
    // Sidebar Layout - Users (NEW)
    path: '',
    canActivate: [ActivateUsers],
    component: SidebarLayout,
    data: { showLanguageSwitcher: true, isNew: true }, // TODO: isNew after bootstrap removed
    children: [
      {
        path: 'my-apps',
        data: { title: 'Menu.MyApplications' },
        loadComponent: () => import('@features/my-apps/my-apps-page').then((m) => m.MyAppsPage),
      },
      {
        path: 'recent-activities',
        data: { title: 'Menu.RecentActivity' },
        loadComponent: () =>
          import('@features/recent-activities/recent-activities-page').then((m) => m.RecentActivitiesPage),
      },
    ],
  },
  {
    // Sidebar Layout - Admin (NEW)
    path: '',
    canActivate: [ActivateAdmin],
    component: SidebarLayout,
    data: { showBreadcrumb: true, title: 'Admin Menu', isNew: true },
    children: [
      {
        path: 'dashboard',
        data: { title: 'Dashboard' },
        loadComponent: () => import('@features/dashboard/dashboard-page').then((m) => m.DashboardPage),
      },
      {
        path: '',
        data: { title: 'Management' },
        children: [
          {
            path: 'users',
            data: { title: 'Users' },
            loadComponent: () => import('@features/management/users/users-page').then((m) => m.UsersPage),
          },
          {
            path: 'groups',
            data: { title: 'Groups' },
            loadComponent: () => import('@features/management/groups/groups-page').then((m) => m.GroupsPage),
          },
          {
            path: 'apps',
            data: { title: 'Appplications' },
            loadComponent: () => import('@features/management/apps/apps-page').then((m) => m.AppsPage),
          },
          {
            path: 'conditional-access',
            data: { title: 'Conditional Access' },
            loadComponent: () => import('@features/management/conditional-access/conditional-access-page').then((m) => m.ConditionalAccessPage),
          },
        ],
      },
      {
        path: 'monitoring',
        data: { title: 'Monitoring' },
        loadComponent: () => import('@features/monitoring/monitoring-page').then((m) => m.MonitoringPage),
      },
      {
        path: '',
        data: { title: 'Reports' },
        children: [
          {
            path: 'embedded-reports',
            data: { title: 'Embedded Reports' },
            loadComponent: () => import('@features/reports/embedded-reports-page').then((m) => m.EmbeddedReportsPage),
          },
          {
            path: 'automated-reports',
            data: { title: 'Automated Reports' },
            loadComponent: () => import('@features/reports/automated-reports-page').then((m) => m.AutomatedReportsPage),
          },
        ],
      },
      {
        path: 'downloads',
        data: { title: 'Downloads' },
        loadComponent: () => import('@features/downloads/downloads-page').then((m) => m.DownloadsPage),
      },
    ],
  },
  {
    // Sidebar Layout - Users
    path: '',
    canActivate: [ActivateUsers],
    component: SidebarLayout,
    data: { showLanguageSwitcher: true },
    children: [
      {
        path: 'auth-error',
        loadComponent: () =>
          import('./authentication/auth-error/auth-error.component').then((m) => m.AuthErrorComponent),
        data: { title: 'Authentication.Error' },
      },
      {
        path: 'system-status',
        loadComponent: () => import('./system-status/system-status.component').then((m) => m.SystemStatusComponent),
        data: { title: 'Menu.SystemStatus' },
      },
      {
        path: 'profile',
        loadComponent: () => import('./profile/profile.component').then((m) => m.ProfileComponent),
        data: { title: 'Menu.MyProfile' },
      },
      {
        path: 'my-profile',
        loadComponent: () => import('./my-profile/my-profile.component').then((m) => m.MyProfileComponent),
        data: { title: 'Menu.MyProfile' },
      },
      {
        path: 'my-profile/:tab',
        loadComponent: () => import('./my-profile/my-profile.component').then((m) => m.MyProfileComponent),
        data: { title: 'Menu.MyProfile' },
      },
      {
        path: 'error',
        loadComponent: () => import('./error/error.component').then((m) => m.ErrorComponent),
        data: { title: 'General.Error' },
      },
      {
        path: 'activity',
        loadChildren: () => import('./user/user.module').then((m) => m.routes),
        data: { title: 'Menu.Management' },
      },
      {
        path: 'whats-new',
        loadComponent: () => import('./whats-new/whats-new.component').then((m) => m.WhatsNewComponent),
        data: { title: 'Menu.WhatsNew' },
      },
    ],
  },
  {
    // Sidebar Layout - Marketing
    path: '',
    canActivate: [ActivateMarketing],
    component: SidebarLayout,
    children: [
      {
        path: 'whats-new-list',
        loadComponent: () =>
          import('./whats-new/whats-new-list/whats-new-list.component').then((m) => m.WhatsNewListComponent),
        data: { title: 'Menu.WhatsNew' },
      },
      {
        path: 'whats-new-add',
        loadComponent: () =>
          import('./whats-new/whats-new-add/whats-new-add.component').then((m) => m.WhatsNewAddComponent),
        data: { title: 'Menu.WhatsNew' },
      },
      {
        path: 'whats-new-edit',
        loadComponent: () =>
          import('./whats-new/whats-new-edit/whats-new-edit.component').then((m) => m.WhatsNewEditComponent),
        data: { title: 'Menu.WhatsNew' },
      },
      {
        path: 'whats-delete-modal',
        loadComponent: () =>
          import('./whats-new/whats-delete-modal/whats-delete-modal.component').then(
            (m) => m.WhatsDeleteModalComponent,
          ),
        data: { title: 'Menu.WhatsNew' },
      },
      {
        path: 'send-notification',
        loadComponent: () =>
          import('./send-notification/send-notification.component').then((m) => m.SendNotificationComponent),
        data: { title: 'Menu.SendNotification' },
      },
    ],
  },
  {
    // Sidebar Layout - Admin
    path: '',
    canActivate: [ActivateAdmin],
    component: SidebarLayout,
    data: { showBreadcrumb: true, title: 'Admin Menu' },
    children: [
      {
        path: '',
        data: { title: 'Management' },
        children: [
          {
            path: 'users',
            loadChildren: () => import('./user/user.module').then((m) => m.routes),
            data: { title: 'Users' },
          },
        ],
      },
      {
        path: 'enterprise',
        loadChildren: () => import('./enterprise/enterprise.module').then((m) => m.routes),
        data: { title: 'Menu.Enterprise' },
      },
      {
        path: 'setup',
        loadChildren: () => import('./setup/setup.module').then((m) => m.routes),
        data: { title: 'Menu.Setup' },
      },
      {
        path: 'integration',
        loadChildren: () => import('./integration/integration.module').then((m) => m.routes),
        data: { title: 'Menu.Integration' },
      },
      {
        path: 'application',
        loadChildren: () => import('./application/application.module').then((m) => m.routes),
        data: { title: 'Menu.Management' },
      },
      {
        path: 'reports',
        loadChildren: () => import('./reports/reports.module').then((m) => m.routes),
        data: { title: 'Menu.Reports' },
      },
      {
        path: 'conditional-access',
        loadChildren: () => import('./conditional-access/conditional-access.module').then((m) => m.routes),
        data: { title: 'Menu.Management' },
      },
      {
        path: 'credential-reset',
        loadChildren: () => import('./credential-reset/credential-reset.module').then((m) => m.routes),
        data: { title: 'Menu.Setup' },
      },
      {
        path: 'cloud',
        loadChildren: () => import('./cloud/directory.module').then((m) => m.routes),
        data: { title: 'Menu.Integration' },
      },
      {
        path: 'premises',
        loadChildren: () => import('./premises/premises.module').then((m) => m.routes),
        data: { title: 'Menu.Integration' },
      },
      {
        path: 'monitoring',
        loadChildren: () => import('./monitoring/monitoring.module').then((m) => m.routes),
        data: { title: 'Menu.Monitoring' },
      },
      {
        path: 'tutorials',
        loadChildren: () => import('./tutorials/tutorials.module').then((m) => m.routes),
        data: { title: 'Menu.Tutorials' },
      },
      {
        path: 'theme/local',
        loadComponent: () => import('./theme/theme.component').then((m) => m.ThemeComponent),
        data: { title: 'Menu.LocalTheme' },
      },
      {
        path: 'super-admin-tenant-warning',
        loadComponent: () =>
          import('./shared/super-admin-tenant-warning/super-admin-tenant-warning.component').then(
            (m) => m.SuperAdminTenantWarningComponent,
          ),
      },
    ],
  },
];
